import { Bill, Guest } from '../types';
import { calculateBreakdown, formatCurrency } from './billCalculator';
import { jsPDF } from 'jspdf';

const SUMMARY_LOGO_SRC = `${process.env.PUBLIC_URL}/receipt.jpg`;

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

const buildSummaries = (guests: Guest[], bill: Bill) => {
  return guests.map(guest => ({
    guest,
    breakdown: calculateBreakdown(guest, bill)
  }));
};

const sanitizeFilename = (extension: string) => `splitcheck.${extension}`;

export const exportBillAsCSV = (bill: Bill, guests: Guest[], currency: string = 'USD', locale?: string) => {
  if (!guests.length) {
    alert('Add at least one guest to export the split.');
    return;
  }

  const summaries = buildSummaries(guests, bill);
  const headers = [
    'Guest',
    'Item',
    'Item Amount',
    'Subtotal',
    'Tax Share',
    'Service Charge',
    'Auto Gratuity',
    'Additional Gratuity',
    'Total'
  ];

  const rows: string[][] = [];
  if (bill.notes.trim()) {
    rows.push(['Notes', bill.notes]);
    rows.push(['', '']);
  }
  summaries.forEach(summary => {
    const { guest, breakdown } = summary;
    rows.push([`${guest.name}:`, formatCurrency(breakdown.total, currency, locale)]);
    rows.push(['', '']);
    rows.push(['Item', 'Cost']);

    if (breakdown.items.length > 0) {
      breakdown.items.forEach(item => {
        rows.push([item.name, formatCurrency(item.amount, currency, locale)]);
      });
    } else {
      rows.push(['No specific items assigned yet.', '']);
    }

    rows.push(['', '']);
    const charges = [
      { label: 'Subtotal', value: breakdown.subtotal },
      { label: 'Tax', value: breakdown.taxShare },
      { label: 'Service Charge', value: breakdown.serviceShare },
      { label: 'Auto Gratuity', value: breakdown.autoGratuityShare },
      { label: 'Additional Gratuity', value: breakdown.additionalGratuityShare },
      { label: 'Total', value: breakdown.total },
    ];

    charges
      .filter(charge => charge.value !== 0)
      .forEach(charge => {
        rows.push([charge.label, formatCurrency(charge.value, currency, locale)]);
      });

    rows.push(['', '']);
    rows.push(['', '']);
  });

  const escapeCell = (value: string) => `"${value.replace(/"/g, '""')}"`;
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => escapeCell(cell)).join(','))
  ].join('\r\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, sanitizeFilename('csv'));
};

const formatDateString = (date: Date) => {
  const safeDate = date instanceof Date ? date : new Date(date);
  return safeDate.toLocaleDateString(undefined, { dateStyle: 'medium' });
};

const loadLogoImage = (): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('Failed to load summary logo'));
  image.src = SUMMARY_LOGO_SRC;
});

const loadLogoDataUrl = async (): Promise<string | null> => {
  try {
    const response = await fetch(SUMMARY_LOGO_SRC);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

export const exportBillAsImage = async (bill: Bill, guests: Guest[], currency: string = 'USD', locale?: string) => {
  if (!guests.length) {
    alert('Add at least one guest to export the split.');
    return;
  }

  const summaries = buildSummaries(guests, bill);
  const brandBlue = '#234c71';
  const mutedBlue = '#6f8ca5';
  const borderBlue = '#2f547d';
  const background = '#f7f6f3';
  const cardFill = '#fbfbf9';
  const restaurantDisplay = bill.notes.trim() || bill.restaurantName.trim() || 'Restaurant';
  const guestCountDisplay = String(guests.length);
  const totalItemsCount = bill.items.length;
  const totalCollected = summaries.reduce((sum, summary) => sum + summary.breakdown.total, 0);

  const cardX = 96;
  const cardWidth = 568;
  const guestGap = 18;
  const totalCardHeight = 90;
  const topPadding = 70;
  const sectionGap = 26;
  const summaryNoteFont = '500 26px "SF Pro Text", "Inter", sans-serif';
  const summaryNoteLineHeight = 30;
  const summaryNoteMaxWidth = cardWidth - 170;
  const summaryNoteOffsetY = 116;
  const summaryCardBottomPadding = 20;

  const wrapTextLines = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] => {
    const normalized = text.trim() || '-';
    const words = normalized.split(/\s+/);
    if (!words.length) return ['-'];
    const lines: string[] = [];
    let currentLine = '';
    words.forEach((word) => {
      const candidate = currentLine ? `${currentLine} ${word}` : word;
      if (ctx.measureText(candidate).width <= maxWidth) {
        currentLine = candidate;
        return;
      }
      if (currentLine) {
        lines.push(currentLine);
      }
      currentLine = word;
    });
    if (currentLine) {
      lines.push(currentLine);
    }
    return lines.length ? lines : ['-'];
  };

  const measureCanvas = document.createElement('canvas');
  const measureCtx = measureCanvas.getContext('2d');
  if (!measureCtx) return;
  measureCtx.font = summaryNoteFont;
  const summaryNoteLines = wrapTextLines(measureCtx, restaurantDisplay, summaryNoteMaxWidth);
  const summaryDateOffsetY = summaryNoteOffsetY + summaryNoteLines.length * summaryNoteLineHeight + 8;
  const statYFromSummaryTop = summaryDateOffsetY + 24;
  const summaryHeight = statYFromSummaryTop + 74 + summaryCardBottomPadding;

  const guestCards = summaries.map((summary) => {
    const itemLines = summary.breakdown.items.slice(0, 2).map((item) => ({
      label: item.name,
      value: formatCurrency(item.amount, currency, locale)
    }));
    const chargeLines = [
      { label: 'Subtotal', value: summary.breakdown.subtotal },
      { label: 'Tax', value: summary.breakdown.taxShare },
      { label: 'Service Charge', value: summary.breakdown.serviceShare },
      { label: 'Auto Gratuity', value: summary.breakdown.autoGratuityShare },
      { label: 'Additional Gratuity', value: summary.breakdown.additionalGratuityShare }
    ]
      .filter((charge) => charge.value !== 0)
      .map((charge) => ({
        label: charge.label,
        value: formatCurrency(charge.value, currency, locale)
      }));
    const lines = [...itemLines, ...chargeLines];
    const lineCount = Math.max(1, lines.length);
    const height = Math.max(154, 88 + lineCount * 22);
    return { ...summary, lines, height };
  });
  const cardsHeight = guestCards.reduce((sum, card) => sum + card.height, 0) + Math.max(0, guestCards.length - 1) * guestGap;
  const width = 760;
  const height = Math.max(1120, topPadding + summaryHeight + sectionGap + cardsHeight + sectionGap + totalCardHeight + 92);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, width, height);

  const drawWaveBackground = () => {
    ctx.strokeStyle = '#ece8df';
    ctx.lineWidth = 2;
    for (let y = -10; y <= height + 30; y += 32) {
      ctx.beginPath();
      ctx.moveTo(-40, y);
      for (let x = -40; x <= width + 40; x += 64) {
        ctx.bezierCurveTo(x + 16, y - 8, x + 32, y + 8, x + 64, y);
      }
      ctx.stroke();
    }
  };

  const drawRoundedRect = (x: number, y: number, rectWidth: number, rectHeight: number, radius: number) => {
    const effectiveRadius = Math.min(radius, rectWidth / 2, rectHeight / 2);
    ctx.beginPath();
    ctx.moveTo(x + effectiveRadius, y);
    ctx.arcTo(x + rectWidth, y, x + rectWidth, y + rectHeight, effectiveRadius);
    ctx.arcTo(x + rectWidth, y + rectHeight, x, y + rectHeight, effectiveRadius);
    ctx.arcTo(x, y + rectHeight, x, y, effectiveRadius);
    ctx.arcTo(x, y, x + rectWidth, y, effectiveRadius);
    ctx.closePath();
  };

  const drawCard = (x: number, y: number, rectWidth: number, rectHeight: number, radius = 18) => {
    drawRoundedRect(x, y, rectWidth, rectHeight, radius);
    ctx.fillStyle = cardFill;
    ctx.fill();
    ctx.strokeStyle = borderBlue;
    ctx.lineWidth = 2;
    ctx.stroke();
  };

  const drawTextTrimmed = (
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    align: CanvasTextAlign = 'left'
  ) => {
    const suffix = '...';
    const trimmedText = text.trim() || '-';
    let candidate = trimmedText;
    ctx.textAlign = align;
    if (ctx.measureText(candidate).width <= maxWidth) {
      ctx.fillText(candidate, x, y);
      return;
    }
    while (candidate.length > 0 && ctx.measureText(`${candidate}${suffix}`).width > maxWidth) {
      candidate = candidate.slice(0, -1);
    }
    ctx.fillText(`${candidate}${suffix}`, x, y);
  };

  const drawWrappedTextLines = (lines: string[], x: number, y: number, lineHeight: number) => {
    lines.forEach((line, index) => {
      ctx.fillText(line, x, y + index * lineHeight);
    });
  };

  drawWaveBackground();

  const summaryY = topPadding;
  drawCard(cardX, summaryY, cardWidth, summaryHeight);
  ctx.fillStyle = brandBlue;
  ctx.font = '600 14px "SF Pro Text", "Inter", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText('SplitCheck', cardX + cardWidth / 2, summaryY + 12);
  ctx.font = '700 40px "SF Pro Display", "Inter", sans-serif';
  ctx.fillText('Summary', cardX + cardWidth / 2, summaryY + 24);

  drawRoundedRect(cardX + cardWidth - 130, summaryY + 24, 96, 96, 16);
  ctx.fillStyle = brandBlue;
  ctx.fill();
  const logoX = cardX + cardWidth - 126;
  const logoY = summaryY + 28;
  const logoSize = 88;
  try {
    const logoImage = await loadLogoImage();
    ctx.save();
    drawRoundedRect(logoX, logoY, logoSize, logoSize, 14);
    ctx.clip();
    ctx.drawImage(logoImage, logoX, logoY, logoSize, logoSize);
    ctx.restore();
  } catch {
    // Keep the solid brand block when logo loading fails.
  }

  ctx.fillStyle = brandBlue;
  ctx.textAlign = 'left';
  ctx.font = summaryNoteFont;
  drawWrappedTextLines(summaryNoteLines, cardX + 28, summaryY + summaryNoteOffsetY, summaryNoteLineHeight);
  ctx.fillStyle = mutedBlue;
  ctx.font = '500 22px "SF Pro Text", "Inter", sans-serif';
  ctx.fillText(formatDateString(bill.date), cardX + 28, summaryY + summaryDateOffsetY);

  const statY = summaryY + statYFromSummaryTop;
  const statCardWidth = (cardWidth - 76) / 2;
  drawRoundedRect(cardX + 20, statY, statCardWidth, 74, 16);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.strokeStyle = '#b9c6d3';
  ctx.lineWidth = 1.5;
  ctx.stroke();
  drawRoundedRect(cardX + 44 + statCardWidth, statY, statCardWidth, 74, 16);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.strokeStyle = '#b9c6d3';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  ctx.fillStyle = mutedBlue;
  ctx.textAlign = 'center';
  ctx.font = '600 18px "SF Pro Text", "Inter", sans-serif';
  ctx.fillText('Guests', cardX + 20 + statCardWidth / 2, statY + 10);
  ctx.fillText('Items', cardX + 44 + statCardWidth + statCardWidth / 2, statY + 10);
  ctx.fillStyle = brandBlue;
  ctx.font = '700 22px "SF Pro Text", "Inter", sans-serif';
  ctx.fillText(guestCountDisplay, cardX + 20 + statCardWidth / 2, statY + 36);
  ctx.textAlign = 'center';
  ctx.fillText(String(totalItemsCount), cardX + 44 + statCardWidth + statCardWidth / 2, statY + 36);

  const guestCardsY = summaryY + summaryHeight + sectionGap;
  let currentGuestCardY = guestCardsY;
  guestCards.forEach((summary) => {
    const cardY = currentGuestCardY;
    drawCard(cardX, cardY, cardWidth, summary.height);

    ctx.fillStyle = brandBlue;
    ctx.font = '700 34px "SF Pro Display", "Inter", sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(summary.guest.name, cardX + 22, cardY + 20);

    ctx.font = '700 34px "SF Pro Display", "Inter", sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(formatCurrency(summary.breakdown.total, currency, locale), cardX + cardWidth - 20, cardY + 20);

    ctx.font = '500 20px "SF Pro Text", "Inter", sans-serif';
    summary.lines.forEach((line, itemIndex) => {
      const lineY = cardY + 66 + itemIndex * 24;
      ctx.fillStyle = '#2b3340';
      ctx.textAlign = 'left';
      drawTextTrimmed(line.label, cardX + 22, lineY, cardWidth - 190);
      ctx.fillStyle = mutedBlue;
      ctx.textAlign = 'right';
      ctx.fillText(line.value, cardX + cardWidth - 22, lineY);
    });

    if (summary.lines.length === 0) {
      ctx.fillStyle = '#667281';
      ctx.textAlign = 'left';
      ctx.fillText('No assigned items', cardX + 22, cardY + 76);
    }
    currentGuestCardY += summary.height + guestGap;
  });

  const totalCardY = guestCardsY + cardsHeight + sectionGap;
  drawCard(cardX, totalCardY, cardWidth, totalCardHeight, 16);
  ctx.fillStyle = brandBlue;
  ctx.font = '700 34px "SF Pro Display", "Inter", sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText('Total Collected', cardX + 22, totalCardY + 24);
  ctx.textAlign = 'right';
  ctx.fillText(formatCurrency(totalCollected, currency, locale), cardX + cardWidth - 22, totalCardY + 24);
  ctx.fillStyle = '#5f6978';
  ctx.font = '500 18px "SF Pro Text", "Inter", sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText('Generated by Scan, Select, SplitCheck.', cardX + 22, totalCardY + 58);

  canvas.toBlob(blob => {
    if (blob) {
      downloadBlob(blob, sanitizeFilename('png'));
    }
  });
};

export const exportBillAsPDF = async (bill: Bill, guests: Guest[], currency: string = 'USD', locale?: string) => {
  if (!guests.length) {
    alert('Add at least one guest to export the split.');
    return;
  }

  const summaries = buildSummaries(guests, bill);
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const margin = 40;
  let y = margin;
  const logoDataUrl = await loadLogoDataUrl();

  doc.setFontSize(20);
  doc.text('SplitCheck', margin, y);
  if (logoDataUrl) {
    const logoSize = 40;
    doc.addImage(logoDataUrl, 'JPEG', doc.internal.pageSize.width - margin - logoSize, y - 16, logoSize, logoSize);
  }
  y += 28;

  doc.setFontSize(12);
  doc.text(`Date: ${formatDateString(bill.date)}`, margin, y);
  y += 18;
  if (bill.restaurantName) {
    doc.text(`Location: ${bill.restaurantName}`, margin, y);
    y += 18;
  }
  doc.text(`Guests: ${guests.length}`, margin, y);
  y += 24;
  if (bill.notes) {
    doc.text(`Notes: ${bill.notes}`, margin, y);
    y += 22;
  }

  const ensureSpace = (required: number) => {
    if (y + required > doc.internal.pageSize.height - margin) {
      doc.addPage();
      y = margin;
    }
  };

  const chargesLabels = [
    { label: 'Subtotal', value: (breakdown: ReturnType<typeof calculateBreakdown>) => breakdown.subtotal },
    { label: 'Tax', value: (breakdown: ReturnType<typeof calculateBreakdown>) => breakdown.taxShare },
    { label: 'Service Charge', value: (breakdown: ReturnType<typeof calculateBreakdown>) => breakdown.serviceShare },
    { label: 'Auto Gratuity', value: (breakdown: ReturnType<typeof calculateBreakdown>) => breakdown.autoGratuityShare },
    { label: 'Additional Gratuity', value: (breakdown: ReturnType<typeof calculateBreakdown>) => breakdown.additionalGratuityShare },
    { label: 'Total', value: (breakdown: ReturnType<typeof calculateBreakdown>) => breakdown.total },
  ];

  summaries.forEach(summary => {
    const { guest, breakdown } = summary;
    ensureSpace(140);

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`${guest.name}: ${formatCurrency(breakdown.total, currency, locale)}`, margin, y);
    y += 20;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    if (breakdown.items.length > 0) {
      breakdown.items.forEach(item => {
        ensureSpace(18);
        doc.text(`${item.name} - ${formatCurrency(item.amount, currency, locale)}`, margin + 8, y);
        y += 16;
      });
    } else {
      ensureSpace(18);
      doc.text('No specific items assigned yet.', margin + 8, y);
      y += 16;
    }

    y += 4;
    chargesLabels.forEach(chargeLine => {
      const chargeValue = chargeLine.value(breakdown);
      if (chargeValue === 0) return;
      ensureSpace(18);
      doc.text(`${chargeLine.label} ${formatCurrency(chargeValue, currency, locale)}`, margin + 8, y);
      y += 16;
    });

    ensureSpace(30);
    const lineY = y + 8;
    doc.setDrawColor(35, 76, 113);
    doc.setLineWidth(2);
    doc.line(margin, lineY, doc.internal.pageSize.width - margin, lineY);
    y = lineY + 16;
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
  });

  doc.save(sanitizeFilename('pdf'));
};
